import { ChangeEvent, KeyboardEvent, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { faHandshake } from '@fortawesome/pro-solid-svg-icons/faHandshake';
import toast from 'react-hot-toast';

import { Avatar } from 'us-react-components/dist/Avatar/Avatar';
import { outlinedButtonClasses } from 'us-react-components/dist/Button/Button';
import { InfoCard } from 'us-react-components/dist/InfoCard/InfoCard';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { AxiosError, HeicConvertService } from 'us-web-services';

import { Button, ButtonType } from '../../../components';
import { useDependencies } from '../../../dependencies';
import { ModalName } from '../../../modals/constants';
import { FlowType } from '../../../models/FamilyRegistrationStore/constants';
import { useModal } from '../../../modals/modalContext';
import { Routes } from '../../../Routes';

const AVATAR_URL = 'https://assets.urbansitter.com/static/img/empty-avatar.svg';

export const Photo = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const modal = useModal();

  const {
    services: { tracker },
    stores: { familyRegistration, user }
  } = useDependencies();

  familyRegistration.setCurrentRoute(Routes.parent.registration.photo);

  const onLabelKeyDown = (e: KeyboardEvent<HTMLLabelElement>): void => {
    const input = document.getElementById('photo');
    if (e.key === 'Enter') {
      e.stopPropagation(); // don't submit the form;
      input?.click();
    }
  }

  const fileRef = useRef<HTMLInputElement>(null);

  const isDefaultAvatar = (): boolean => !!user.user?.pictureUrl?.endsWith('us-default-avatar-320.png');

  const handleUpload = async (selectedFile: File): Promise<void> => {
    if (fileRef.current && selectedFile) {
      const trackingProps = {
        elementId: fileRef.current.id,
        elementText: t('pages.nux.family.photo.uploadPhoto'),
      };

      await tracker.track('clicked-button', trackingProps);

      const file: File = selectedFile;
      const buffer = await file.arrayBuffer();

      if (HeicConvertService.isHeic(new Uint8Array(buffer))) {
        const postData = new FormData();

        try {
          postData.append('file', new Blob([buffer]));

          const response = (await HeicConvertService.convert(postData)).data.data;
          const base64 = btoa(
            response.data.reduce((res: string, byte: number) => res + String.fromCharCode(byte), '')
          );

          user.setTemporaryPhoto(`data:image/png;base64,${base64}`);
          modal?.setModalName(ModalName.Cropper);
        } catch (e) {
          if (e instanceof AxiosError && e.response?.data?.error) {
            toast.error(e.response.data.error);
          } else {
            toast.error(t('notifications.genericError'));
          }
        }
      } else {
        user.setTemporaryPhoto(URL.createObjectURL(file));
        modal?.setModalName(ModalName.Cropper);
      }
    }
  }


  const handleContinue = async (): Promise<void> => {
    if (familyRegistration.flowType !== FlowType.HouseholdServices) {
      navigate(Routes.parent.registration.count);
    } else {
      navigate(Routes.parent.registration.profilePreview);
    }
  }

  const onInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (event?.currentTarget?.files?.length) {
      handleUpload(event.currentTarget.files[0]);
    }
  }

  const onInputClick = (): void => {
    if (fileRef.current?.value) {
      fileRef.current.value = ''; // fixes launching modal via onChange when same file is re-selected
    }
  }

  const renderInfoCard = (): JSX.Element => (
    <InfoCard
      iconName={faHandshake}
      title={t('pages.nux.family.photo.infoCard.title')}
      description={t('pages.nux.family.photo.infoCard.description')}
    />
  );

  const getUploadPhotoClasses = (): string => {
    if (isDefaultAvatar()) {
      // mimic primary button
      return 'cursor-pointer text-white px-6 py-2 rounded-full font-semibold bg-sapphire hover:bg-sapphire-700 active:bg-sapphire-100 disabled:text-white disabled:bg-neutral-500 disabled:border-neutral-500 disabled:pointer-events-none';
    }

    // mimic secondary button
    return 'cursor-pointer text-sapphire border-sapphire py-[0.4375rem] px-6 rounded-full bg-transparent border ' + outlinedButtonClasses;
  }

  const handleSkip = async (): Promise<void> => {
    handleContinue();
  }

  /* eslint-disable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions */
  return (
    <NuxContainer
      familyRegistration={familyRegistration}
      navigate={navigate}
      title={t('pages.nux.family.photo.title')}
      tracker={tracker}
      infoCard={renderInfoCard()}
      onContinue={handleContinue}
      isContinueDisabled={isDefaultAvatar()}>
      <div className='w-[90%] md:w-[70%]'>
        <div className='flex flex-col items-center justify-center xs:pt-2 sm:pt-8'>
          <div className='flex rounded-full border-[1px] border-emerald w-[116px] h-[116px] justify-center items-center'>
            <Avatar className='w-[110px] h-[110px]' imageUrl={user.user?.pictureUrl ?? AVATAR_URL} />
          </div>
          <div className='min-w-[200px] mt-8 flex justify-center'>
            <label tabIndex={0} htmlFor='photo'
                   onKeyDown={onLabelKeyDown}
                   className={getUploadPhotoClasses()}>
              {t('pages.nux.family.photo.uploadPhoto')}
            </label>
            <input
              id='photo'
              ref={fileRef}
              className='hidden'
              type='file'
              accept='image/*, .heic'
              onChange={onInputChange}
              onClick={onInputClick}
            />
          </div>
          <Button className='underline pt-4' buttonType={ButtonType.tertiary} id='skip-photo' onClick={handleSkip}>
            {t('pages.nux.family.photo.skipForNow')}
          </Button>
        </div>
      </div>
    </NuxContainer>
  )
});
